import React, { useCallback, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import {
  useAddAttendee,
  useCheckAttendeeDuplicateEmailNationalIdAPONumber,
  useGetEventAttendees,
} from "../../hooks/useAttendee";
import { toast } from "react-toastify";
import { useCountry } from "../../hooks/useCountry";
import { useAddDoctorsMasterData } from "../../hooks/useDoctorMasterData";
import { useEditEvent, useGetEventDataById } from "../../hooks/useEvent";
import { getEventStatus } from "./EventStatus";
import { useUser } from "../../hooks/useUser";
import doctorMasterDataService from "../../services/doctorMasterDataService";
import debounce from "lodash.debounce";

export default function AddNewAttendeeAttendEvent({ open, onClose, eventId }) {
  const [errors, setErrors] = useState({});
  const [originalDoctorData, setOriginalDoctorData] = useState(null);
  const [isDoctorDataFound, setIsDoctorDataFound] = useState(false);
  const [isAbbottDoctor, setIsAbbottDoctor] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [values, setValues] = useState({
    event_id: eventId,
    national_id: "",
    first_name: "",
    family_name: "",
    email: "",
    phone_number: "",
    country_id: "",
    // city_id: "",
    organization: "",
    specialty: "",
    APO_number: "",
    medical_license: "",
    signature: "No Signature",
    doctor_master_data_id: "",
    industry_area: "",
    updated_at: null,
    created_at: null,
  });
  const { mutate: addAttendee } = useAddAttendee();
  const { data: countryData } = useCountry();
  // const { data: filteredCities } = useCountryCities(values?.country_id);
  const { data: attendeesData } = useGetEventAttendees(eventId);
  const { data: eventData } = useGetEventDataById(eventId);
  const { mutate: editEvent } = useEditEvent();
  const { mutateAsync: addDoctorMasterData } = useAddDoctorsMasterData();
  // const { mutateAsync: editDoctorMasterData } = useEditDoctorsMasterData();
  const { mutateAsync: checkDuplicate } =
    useCheckAttendeeDuplicateEmailNationalIdAPONumber();
  const { data: users } = useUser();

  const searchDoctor = useCallback(
    // Added lodash.debounce to delay the search until typing stops.
    debounce(async (value) => {
      if (!value.trim()) return;

      try {
        const foundDoctor = await doctorMasterDataService.findDoctor(
          "national_id",
          value.trim()
        );

        if (foundDoctor) {
          setOriginalDoctorData(foundDoctor);

          const isAbbott = foundDoctor.organization?.toLowerCase() === "abbott";
          setIsAbbottDoctor(isAbbott);

          const autoFilledValues = {
            first_name: foundDoctor.first_name || "",
            family_name: foundDoctor.family_name || "",
            email: foundDoctor.email || "",
            phone_number: foundDoctor.phone_number || "",
            country_id: foundDoctor.country_id || "",
            // city_id: foundDoctor.city_id || "",
            organization: foundDoctor.organization || "",
            specialty: foundDoctor.specialty || "",
            APO_number: foundDoctor.APO_number || "",
            medical_license: foundDoctor.medical_license || "",
            doctor_master_data_id: foundDoctor.id || "",
            industry_area: foundDoctor.industry_area || "",
          };

          if (isAbbott) {
            const user = users?.find(
              (u) => u.email_address === autoFilledValues.email || values.email
            );
            if (user) {
              autoFilledValues.APO_number = `${user.UPI_num}` || "";
            }
          }

          setValues((prev) => ({
            ...prev,
            ...autoFilledValues,
          }));

          toast.info("Attendee data found and auto-completed");
          setIsDoctorDataFound(true);
        } else {
          setOriginalDoctorData(null);
          setIsAbbottDoctor(false);

          setValues((prev) => ({
            ...prev,
            first_name: "",
            family_name: "",
            email: "",
            phone_number: "",
            country_id: "",
            // city_id: "",
            organization: "",
            specialty: "",
            APO_number: "",
            medical_license: "",
            doctor_master_data_id: "",
            industry_area: "",
          }));
          setIsDoctorDataFound(false);
          toast.warning("No doctor found with this National ID");
        }
      } catch (error) {
        // console.error("Error searching doctor:", error);
        toast.error("Failed to search doctor master data");
      }
    }, 600), // 👈 wait 600ms after typing stops
    [users, values.email]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "organization") {
      if (value.toLowerCase() === "abbott") {
        setIsAbbottDoctor(true);
      } else {
        setIsAbbottDoctor(false);
      }
    }

    if (name === "national_id") {
      searchDoctor(value);
    }
  };

  const validationRules = {
    national_id: {
      required: "National ID is required",
      pattern: "National ID must be a number",
    },
    first_name: {
      required: "First Name is required",
      minLength: "First Name must be at least 3 characters",
    },
    family_name: {
      required: "Family Name is required",
      minLength: "Family Name must be at least 3 characters",
    },
    email: {
      required: "Email is required",
      pattern: "Email must be a valid email address",
    },
    phone_number: isAbbottDoctor
      ? {}
      : {
          required: "Phone Number is required",
          pattern: "Phone Number must be a number",
        },
    // country_id: {
    //   required: "Country is required",
    // },
    // city_id: {
    //   required: "City is required",
    // },
    specialty: {
      required: "Specialty is required",
    },
    medical_license: {
      required: "Medical License is required",
    },
  };

  const validateForm = (formData) => {
    const newErrors = {};

    // Iterate through each field in the validationRules
    for (const field in validationRules) {
      const value = formData[field] || "";
      const rules = validationRules[field];
      // Check for required
      if (rules.required) {
        if (typeof value === "string" && !value.trim()) {
          newErrors[field] = rules.required;
          continue;
        } else if (typeof value === "number" && value === 0) {
          newErrors[field] = rules.required;
          continue;
        }
      }

      // Check for minLength
      if (
        rules.minLength &&
        value.length < (rules.minLength.match(/\d+/) || [])[0]
      ) {
        newErrors[field] = rules.minLength;
        continue;
      }

      // Check for maxLength
      if (
        rules.maxLength &&
        value.length > (rules.maxLength.match(/\d+/) || [])[0]
      ) {
        newErrors[field] = rules.maxLength;
        continue;
      }

      // Check for pattern
      if (rules.pattern) {
        let regex;
        if (field === "email") {
          regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email regex
        } else if (field === "national_id") {
          regex = /^\d+$/; // Numbers only
        }
        if (regex && !regex.test(value)) {
          newErrors[field] = rules.pattern;
        }
      }
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      const validationErrors = validateForm(values);

      if (Object.keys(validationErrors).length > 0) {
        toast.error("Please fill all required fields correctly");
        return;
      }

      // Check for duplicates before adding
      const duplicateCheck = await checkDuplicate({
        eventId,
        email: values.email.trim(),
        national_id: values.national_id.trim(),
        apo_number: values.APO_number.trim(),
      });

      if (duplicateCheck?.duplicateEmail) {
        toast.error(`Email ${values.email} already exists in this event`);
        return;
      }
      if (duplicateCheck?.duplicateNationalId) {
        toast.error(
          `National ID ${values.national_id} already exists in this event`
        );
        return;
      }
      if (duplicateCheck?.duplicateApoNumber && values.APO_number) {
        toast.error(
          `APO Number ${values.APO_number} already exists in this event`
        );
        return;
      }

      let doctorMasterDataId = values.doctor_master_data_id;

      // If we have original doctor data, compare and update if needed
      // if (originalDoctorData) {
      //   const hasChanges = Object.keys(values).some((key) => {
      //     if (
      //       key === "event_id" ||
      //       key === "signature" ||
      //       key === "doctor_master_data_id"
      //     )
      //       return false;
      //     return values[key] !== originalDoctorData[key];
      //   });

      //   if (hasChanges) {
      //     try {
      //       const updatedDoctorData = {
      //         national_id: values.national_id,
      //         first_name: values.first_name,
      //         family_name: values.family_name,
      //         email: values.email,
      //         phone_number: values.phone_number,
      //         country_id: values.country_id,
      //         city_id: values.city_id,
      //         organization: values.organization,
      //         specialty: values.specialty,
      //         APO_number: values.APO_number,
      //         medical_license: values.medical_license,
      //         industry_area: values.industry_area,
      //       };
      //       await editDoctorMasterData({
      //         id: originalDoctorData.id,
      //         data: updatedDoctorData,
      //       });
      //       toast.info("Doctor master data updated with new information");
      //     } catch (error) {
      //       toast.error("Failed to update doctor master data");
      //       return;
      //     }
      //   }
      // } else
      if (!doctorMasterDataId) {
        // If no doctor master data ID exists, add the doctor to master data first
        const doctorData = {
          national_id: values.national_id,
          first_name: values.first_name,
          family_name: values.family_name,
          email: values.email,
          phone_number: values.phone_number,
          country_id: values.country_id,
          // city_id: values.city_id,
          organization: values.organization,
          specialty: values.specialty,
          APO_number: values.APO_number,
          medical_license: values.medical_license,
          industry_area: values.industry_area,
        };

        try {
          const newDoctorRef = await addDoctorMasterData(doctorData);
          doctorMasterDataId = newDoctorRef.id;
        } catch (error) {
          toast.error("Failed to add doctor to master data");
          return;
        }
      }

      // Add the attendee with the updated doctor master data ID
      addAttendee({
        ...values,
        doctor_master_data_id: doctorMasterDataId,
        created_at: new Date().toISOString(),
      });

      const currentStatus = getEventStatus(
        eventData.start_date,
        eventData.end_date,
        attendeesData,
        eventData.spend_value,
        eventData.organization_id,
        eventData.tov_exported
      );
      if (currentStatus.text !== eventData.status) {
        editEvent({ id: eventData.id, data: { status: currentStatus.text } });
      }

      setValues({
        event_id: eventId,
        national_id: "",
        first_name: "",
        family_name: "",
        email: "",
        phone_number: "",
        country_id: "",
        // city_id: "",
        organization: "",
        specialty: "",
        APO_number: "",
        medical_license: "",
        signature: "No Signature",
        doctor_master_data_id: "",
        industry_area: "",
      });
      setOriginalDoctorData(null);
      setIsDoctorDataFound(false);

      toast.success("Attendee added successfully");
      onClose();
    } catch (error) {
      toast.error("Failed to add attendee");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle className="bg-blueColor text-white py-4">
          Add New Attendee
        </DialogTitle>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div className="col-span-2">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                National ID <span className="text-red-500">*</span>
              </label>
              <input
                name="national_id"
                onChange={handleChange}
                value={values.national_id}
                type="number"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.national_id && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.national_id}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                name="first_name"
                onChange={handleChange}
                value={values.first_name}
                type="text"
                disabled={isDoctorDataFound}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {errors.first_name && (
                <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Family Name <span className="text-red-500">*</span>
              </label>
              <input
                name="family_name"
                onChange={handleChange}
                value={values.family_name}
                type="text"
                disabled={isDoctorDataFound}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {errors.family_name && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.family_name}
                </p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                value={values.email}
                onChange={handleChange}
                type="email"
                disabled={isDoctorDataFound}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex">
                <input
                  type="text"
                  value="+966"
                  disabled
                  className="w-16 p-2 border rounded-l bg-gray-100"
                />
                <input
                  name="phone_number"
                  onChange={handleChange}
                  value={values.phone_number}
                  type="text"
                  disabled={isDoctorDataFound}
                  className="w-full p-2 border rounded-r focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {errors.phone_number && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.phone_number}
                </p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Country
                {/* <span className="text-red-500">*</span> */}
              </label>
              <select
                name="country_id"
                onChange={handleChange}
                value={values.country_id}
                disabled={isDoctorDataFound}
                className="w-full p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="" hidden>
                  Select Country
                </option>
                {countryData?.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country_id && (
                <p className="text-red-500 text-sm mt-1">{errors.country_id}</p>
              )}
            </div>
            {/* <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                City
              </label>
              <select
                disabled={!values.country_id || isDoctorDataFound}
                name="city_id"
                onChange={handleChange}
                value={values.city_id}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="" hidden>
                  Select City
                </option>
                {filteredCities?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
              {errors.city_id && (
                <p className="text-red-500 text-sm mt-1">{errors.city_id}</p>
              )}
            </div> */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Organization
              </label>
              <input
                name="organization"
                onChange={handleChange}
                value={values.organization}
                type="text"
                disabled={isDoctorDataFound}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {errors.organization && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.organization}
                </p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Specialty <span className="text-red-500">*</span>
              </label>
              <input
                name="specialty"
                onChange={handleChange}
                value={values.specialty}
                type="text"
                disabled={isDoctorDataFound}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {errors.specialty && (
                <p className="text-red-500 text-sm mt-1">{errors.specialty}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Medical License <span className="text-red-500">*</span>
              </label>
              <input
                name="medical_license"
                onChange={handleChange}
                value={values.medical_license}
                type="text"
                disabled={isDoctorDataFound}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {errors.medical_license && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.medical_license}
                </p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Practice/Sector
              </label>
              <select
                name="industry_area"
                onChange={handleChange}
                value={values.industry_area}
                className="w-full p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select Practice/Sector
                </option>
                <option value="private">Private</option>
                <option value="public">Public</option>
              </select>
              {errors.industry_area && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.industry_area}
                </p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                APO <span className="text-red-500">*</span>
              </label>
              <input
                name="APO_number"
                onChange={handleChange}
                value={values.APO_number}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              disabled={isAdding}
              data-track="add-attendee-button"
              data-module="Attendees"
              onClick={handleSubmit}
              className={`px-4 py-2 rounded text-white ${
                isAdding ? "bg-gray-400 cursor-not-allowed" : "bg-blueColor "
              }`}
            >
              {isAdding ? "Saving..." : "Save"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
